"use client";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import warning from "@/assets/warning.svg";

import { MoreVertical, Pencil, Trash2, Eye } from "lucide-react";
import React from "react";
import { useToast } from "@/components/ui/use-toast";

import { FacilityBooking } from "@/types/facility/booking";
import { useDeleteFacilityBookingMutation } from "@/services/faciltiesApi";
import { ErrorType } from "@/types";
import type { CalendarEvent } from "./calendar";

interface CalendarEventActionsProps {
  event: CalendarEvent;
  onEdit?: (event: CalendarEvent) => void;
  onView?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
  refetch?: () => void;
  access?: string;
  size?: "sm" | "md" | "lg";
  variant?: "dropdown" | "buttons";
}

export function CalendarEventActions({
  event,
  onEdit,
  onView,
  onDelete,
  refetch,
  access,
  size = "sm",
  variant = "dropdown",
}: CalendarEventActionsProps) {
  const { toast } = useToast();
  const [isDelete, setIsDelete] = React.useState(false);
  const [deleteBooking, { isLoading: deleteLoading }] =
    useDeleteFacilityBookingMutation();

  // Get the original booking data from the event
  const bookingData = event.originalData as FacilityBooking;

  const handleDelete = async () => {
    if (!bookingData?.id || !refetch) return;

    try {
      const resp = await deleteBooking(bookingData.id as number).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Facility Booking Deleted Successfully",
        });
        setIsDelete(false);
      }
    } catch (error) {
      console.error("Error deleting booking:", error);
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(event);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.(event);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDelete(true);
  };

  const canEdit = bookingData?.status === "Pending" && bookingData?.payment_status != "Paid";
  const canDelete = access === "full_access" && bookingData?.status === "Pending";

  if (variant === "buttons") {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        {onView && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0"
            onClick={handleView}
            title="View booking"
          >
            <Eye className="h-3 w-3" />
          </Button>
        )}
        {onEdit && canEdit && (
          <Button
            access={access == "write" || access == "full_access"}
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0"
            onClick={handleEdit}
            title="Edit booking"
          >
            <Pencil className="h-3 w-3" />
          </Button>
        )}
        {onDelete && canDelete && (
          <Button
            access={access == "full_access"}
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
            onClick={handleDeleteClick}
            title="Delete booking"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}

        {/* Delete confirmation dialog */}
        {isDelete && (
          <AlertDialog open={isDelete} onOpenChange={setIsDelete}>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogDescription>
                  <div className="flex flex-col items-center justify-center gap-4">
                    <img src={warning} alt="warning" className="w-18 h-18" />
                    <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                      Please confirm if you want to delete this Facility Booking
                    </AlertDialogTitle>
                  </div>
                  <div className="w-full flex justify-between items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDelete(false);
                      }}
                      className="w-full border border-primary font-semibold"
                    >
                      <i className="fa fa-xmark text-base px-1"></i>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                      className="w-full bg-primary !text-black font-semibold"
                      disabled={deleteLoading}
                    >
                      <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      {deleteLoading ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    );
  }

  // Default dropdown variant
  return (
    access !== "no_access" && <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            className={`flex p-0 data-[state=open]:bg-muted ${size === "sm" ? "h-6 w-6" : size === "md" ? "h-8 w-8" : "h-10 w-10"
              }`}
          >
            <MoreVertical className={`${size === "sm" ? "h-3 w-3" : size === "md" ? "h-4 w-4" : "h-5 w-5"}`} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-40"
          onClick={(e) => e.stopPropagation()}
        >
          {onView && (
            <DropdownMenuItem onClick={handleView}>
              <Eye className="mr-2 h-4 w-4" />
              View
            </DropdownMenuItem>
          )}
          {onEdit && canEdit && (
            <DropdownMenuItem onClick={handleEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
          )}
          {onDelete && canDelete && (
            <DropdownMenuItem onClick={handleDeleteClick}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete confirmation dialog */}
      {
        isDelete && (
          <AlertDialog open={isDelete} onOpenChange={setIsDelete}>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogDescription>
                  <div className="flex flex-col items-center justify-center gap-4">
                    <img src={warning} alt="warning" className="w-18 h-18" />
                    <AlertDialogTitle className="text-xl font-semibold w-80 text-center">
                      Please confirm if you want to delete this Facility Booking
                    </AlertDialogTitle>
                  </div>
                  <div className="w-full flex justify-between items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDelete(false);
                      }}
                      className="w-full border border-primary font-semibold"
                    >
                      <i className="fa fa-xmark text-base px-1"></i>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete();
                      }}
                      className="w-full bg-primary !text-black font-semibold"
                      disabled={deleteLoading}
                    >
                      <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      {deleteLoading ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
            </AlertDialogContent>
          </AlertDialog>
        )
      }
    </>
  );
}
